import React, { useState, useRef, useEffect } from 'react';
import { 
  Input, 
  Box, 
  List, 
  ListItem, 
  Text, 
  useOutsideClick,
  Spinner,
  Flex
} from 'components-lib';
import { useAutocomplete } from './AutocompleteProvider';

/**
 * SmartAutocompleteInput Component
 * 
 * An intelligent autocomplete input that integrates with the AutocompleteProvider
 * to show API-based suggestions while typing. Supports custom input and provides
 * a smooth user experience with keyboard navigation.
 * 
 * Features:
 * - Real-time API-based suggestions
 * - Custom input support (allows typing values not in suggestions)
 * - Keyboard navigation (arrow keys, enter, escape)
 * - Visual feedback for loading states
 * - Automatic suggestion filtering
 * - Click outside to close
 * - Highlight matching text
 */
const SmartAutocompleteInput = ({
  value = '',
  onChange,
  onSelect,
  placeholder = 'Start typing...',
  category,
  subcategory = null,
  allowCustomInput = true,
  disabled = false,
  size = 'md',
  variant = 'outline',
  ...inputProps
}) => {
  const { getFilteredSuggestions, isLoading } = useAutocomplete();
  
  const [inputValue, setInputValue] = useState(value);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  
  const containerRef = useRef();
  const inputRef = useRef();

  // Handle clicks outside to close suggestions
  useOutsideClick({
    ref: containerRef,
    handler: () => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    },
  });

  // Update input value when prop changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Update filtered suggestions when input changes
  useEffect(() => {
    if (inputValue && inputValue.length >= 1) {
      const suggestions = getFilteredSuggestions(category, subcategory, inputValue);
      setFilteredSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0 || allowCustomInput);
    } else {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
    }
    setSelectedIndex(-1);
  }, [inputValue, category, subcategory, getFilteredSuggestions, allowCustomInput]);

  /**
   * Handle input value changes
   */
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    // Call parent onChange if provided
    if (onChange) {
      onChange(e);
    }
  };

  /**
   * Handle suggestion selection
   */
  const handleSelectSuggestion = (suggestion, isFromKeyboard = false) => {
    const selectedValue = typeof suggestion === 'string' ? suggestion : suggestion.complaint || '';
    
    setInputValue(selectedValue);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    
    // Call parent onSelect callback
    if (onSelect) {
      onSelect(selectedValue, {
        isFromSuggestions: true,
        isFromKeyboard,
        originalSuggestion: suggestion
      });
    }
    
    // Focus back to input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  /**
   * Handle custom input selection (when user presses Enter with no suggestion selected)
   */
  const handleCustomInput = () => {
    if (allowCustomInput && inputValue.trim()) {
      setShowSuggestions(false);
      
      if (onSelect) {
        onSelect(inputValue.trim(), {
          isFromSuggestions: false,
          isCustomInput: true
        });
      }
    }
  };

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = (e) => {
    if (!showSuggestions) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : filteredSuggestions.length - 1
        );
        break;
        
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && filteredSuggestions[selectedIndex]) {
          handleSelectSuggestion(filteredSuggestions[selectedIndex], true);
        } else {
          handleCustomInput();
        }
        break;
        
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
        
      default:
        break;
    }
  };

  /**
   * Handle input focus
   */
  const handleFocus = () => {
    if (inputValue && inputValue.length >= 1) {
      setShowSuggestions(true);
    }
  };

  /**
   * Highlight matching text in suggestions
   */
  const highlightMatch = (text, highlight) => {
    if (!highlight) return text;
    
    const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === highlight.toLowerCase() ? (
        <Text as="span" key={index} bg="yellow.200" fontWeight="bold">
          {part}
        </Text>
      ) : part
    );
  };

  return (
    <Box position="relative" ref={containerRef} width="100%">
      <Input
        ref={inputRef}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        placeholder={placeholder}
        disabled={disabled}
        size={size}
        variant={variant}
        autoComplete="off"
        {...inputProps}
      />
      
      {/* Loading indicator */}
      {isLoading && (
        <Box
          position="absolute"
          right="8px"
          top="50%"
          transform="translateY(-50%)"
          zIndex="tooltip"
        >
          <Spinner size="sm" color="blue.500" />
        </Box>
      )}
      
      {/* Suggestions dropdown */}
      {showSuggestions && (
        <Box
          position="absolute"
          zIndex="dropdown"
          width="100%"
          maxH="200px"
          overflowY="auto"
          bg="white"
          boxShadow="lg"
          borderRadius="md"
          mt={1}
          border="1px solid"
          borderColor="gray.200"
        >
          {filteredSuggestions.length > 0 ? (
            <List spacing={0} p={0}>
              {filteredSuggestions.map((suggestion, index) => {
                const displayText = typeof suggestion === 'string' ? suggestion : suggestion.complaint || '';
                const severity = typeof suggestion === 'object' ? suggestion.severity : '';
                
                return (
                  <ListItem
                    key={index}
                    p={3}
                    bg={selectedIndex === index ? 'blue.50' : 'white'}
                    borderBottom={index < filteredSuggestions.length - 1 ? '1px solid' : 'none'}
                    borderColor="gray.100"
                    _hover={{ 
                      bg: selectedIndex === index ? 'blue.100' : 'gray.50',
                      cursor: 'pointer' 
                    }}
                    onClick={() => handleSelectSuggestion(suggestion)}
                  >
                    <Flex justify="space-between" align="center">
                      <Text fontSize="sm">
                        {highlightMatch(displayText, inputValue)}
                      </Text>
                      {severity && (
                        <Text fontSize="xs" color="gray.500" ml={2}>
                          {severity}
                        </Text>
                      )}
                    </Flex>
                  </ListItem>
                );
              })}
            </List>
          ) : allowCustomInput && inputValue.trim() ? (
            // Show custom input option when no suggestions match
            <Box p={3} bg="gray.50" borderRadius="md">
              <Text fontSize="sm" color="gray.600" fontStyle="italic">
                Press Enter to add "{inputValue}"
              </Text>
            </Box>
          ) : null}
        </Box>
      )}
    </Box>
  );
};

export default SmartAutocompleteInput;